import { AddOrEditFileReferenceSlideover } from "@/components/features/matter/file-repo/AddOrEditFileReferenceSlideover";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  LoadingSpinner,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { searchContactsQueryOptions } from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterFileReference,
  useDeleteMatterFileReference,
} from "@/lib/data/react-query/matters/matter-file";
import type { MATTER_TEMPLATE_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type Matter,
  type MatterFile,
  type MatterFileReference,
  type MatterSubItemType,
} from "@/lib/types";
import { getMatterFileReferenceParentPayload } from "@/lib/utils/matterFile";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { startCase, uniq } from "lodash";
import { ChevronDown, WandSparkles } from "lucide-react";
import { useState } from "react";

export const FileReferences = ({
  files,
  matter,
  subItem,
  hideCreateButton = false,
  createFileReferenceOpen,
  onCreateFileReferenceOpenChange,
  createOnly = false,
}: {
  files?: (MatterFile & {
    fileReferenceId: MatterFileReference["id"];
  })[];
  matter?: Matter;
  subItem: { id: string; type: MatterSubItemType };
  hideCreateButton?: boolean;
  createFileReferenceOpen?: boolean;
  onCreateFileReferenceOpenChange?: (open: boolean) => void;
  createOnly?: boolean;
}) => {
  const { hasActionPermission } = useUser();
  const hasViewPermission = Boolean(true);
  const [isCreateFileReferenceOpen, setIsCreateFileReferenceOpen] =
    useState(false);
  const isCreateFileReferenceControlled = createFileReferenceOpen !== undefined;
  const isCreateOpen = createFileReferenceOpen ?? isCreateFileReferenceOpen;
  const setIsCreateOpen = (open: boolean) => {
    if (!isCreateFileReferenceControlled) {
      setIsCreateFileReferenceOpen(open);
    }
    onCreateFileReferenceOpenChange?.(open);
  };
  const deleteMatterFileReference = useDeleteMatterFileReference();
  const { openGenerateDocumentModal } = useGlobalModal();
  const createMatterFileReference = useCreateMatterFileReference();
  const { isLoading } = useQuery(retrieveMatterQueryOptions(matter?.id || ""));
  const isReferencesLoading =
    isLoading ||
    createMatterFileReference.isPending ||
    deleteMatterFileReference.isPending;

  const contactIds = matter
    ? uniq(
        getMatterContactConnections(matter).map(
          (connection) => connection.contactId,
        ),
      )
    : [];

  const { data: contactsData } = useSuspenseQuery({
    ...searchContactsQueryOptions({
      contactIds,
      matterIds: matter ? [matter.id] : [],
    }),
  });

  const contacts = contactsData.data;

  const handleCreateMatterFileReference = async (
    selectedFiles: MatterFile[],
  ) => {
    await Promise.all(
      selectedFiles.map((file) =>
        createMatterFileReference.mutateAsync({
          matterId: matter?.id ?? "",
          fileId: file.id,
          ...getMatterFileReferenceParentPayload(subItem),
        }),
      ),
    );
    setIsCreateOpen(false);
  };

  const createFileReferenceModal = (
    <AddOrEditFileReferenceSlideover
      open={isCreateOpen}
      setOpen={setIsCreateOpen}
      matter={matter}
      handleCreateMatterFileReference={handleCreateMatterFileReference}
      isCreating={createMatterFileReference.isPending}
    />
  );

  if (createOnly) {
    return createFileReferenceModal;
  }

  return (
    <div className="grid h-fit w-full grid-cols-2 gap-4 p-6">
      <div></div>

      {hasActionPermission(
        ACTION_PERMISSION_KEYS.matterFile,
        ACTION_PERMISSION_VALUES.create,
      ) &&
        !hideCreateButton && (
          <div className="ml-auto flex">
            <Button
              variant="outline"
              className="rounded-r-none border-r-0"
              type="button"
              size="sm"
              onClick={() => {
                setIsCreateOpen(true);
              }}
            >
              New file reference
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-l-none px-2"
                  size="sm"
                  title="Additional options"
                  type="button"
                  disabled={!matter}
                >
                  <ChevronDown className="h-4 w-4" />
                  <span className="sr-only">Additional options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    if (!matter) {
                      return;
                    }
                    openGenerateDocumentModal({
                      matter,
                      contacts,
                      afterGenerateFn: (results) => {
                        void handleCreateMatterFileReference(results);
                      },
                      templateType: startCase(
                        subItem.type,
                      ) as (typeof MATTER_TEMPLATE_CATEGORY_OPTIONS)[number],
                      subItem,
                    });
                  }}
                  disabled={!matter}
                >
                  <WandSparkles className="mr-2 h-4 w-4" />
                  Generate document
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      <div className="col-span-full gap-2">
        {!hasViewPermission && (
          <Typography variant="subHeading" className="text-muted-foreground">
            You do not have permission to view file references.
          </Typography>
        )}
        {hasViewPermission && !isReferencesLoading && files?.length === 0 && (
          <Typography variant="subHeading" className="text-muted-foreground">
            No file references have been added.
          </Typography>
        )}
        {isReferencesLoading ? (
          <div className="flex justify-center">
            <LoadingSpinner />
          </div>
        ) : (
          files &&
          files.map((reference) => (
            <div className="mb-2" key={reference.fileReferenceId}>
              <FileViewer
                fileType="matter-file"
                file={{
                  fileName: reference.name,
                  presignedURLKey: reference.presignedUrlKey || "",
                }}
                handleRemoveFileUpload={() => {
                  deleteMatterFileReference.mutate({
                    matterId: reference.matterId,
                    id: reference.fileReferenceId,
                  });
                }}
                imageClassName=" w-auto"
              />
            </div>
          ))
        )}
      </div>
      {createFileReferenceModal}
    </div>
  );
};
